var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var CaseConfig_1;
/* eslint-disable import/no-duplicates */
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchema
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import './caseCondition'; // force import for JsonSchema
import { CaseCondition } from './caseCondition';
import './caseFilterCondition'; // force import for JsonSchema
import { CaseFilterCondition } from './caseFilterCondition';
import { FormulaValidationError, FormulaValidationInfo } from './formulaValidationInfo';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { isSourceValid, validateSourceWithDetail } from './validation/validateHelper';
const schema = {
    $id: 'urn:wisepipe-common-pipe:CaseConfig',
    type: 'object',
    properties: {
        alias: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:StringToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        },
        conditions: {
            type: 'array',
            items: {
                oneOf: [
                    { $ref: 'urn:wisepipe-common-pipe:CaseCondition' },
                    { $ref: 'urn:wisepipe-common-pipe:CaseFilterCondition' }
                ]
            },
            minItems: 1,
            maxItems: 100
        },
        otherwise: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:StringToolVariable'
                },
                { type: 'string', minLength: 1 }
            ]
        }
    },
    required: ['alias', 'conditions']
};
let CaseConfig = CaseConfig_1 = class CaseConfig extends ToolConfig {
    constructor(alias, conditions, otherwise, customFormats) {
        super(ToolType.CASE, customFormats);
        this.alias = alias;
        this.conditions = conditions;
        if (otherwise)
            this.otherwise = otherwise;
        JSON.validate(this, CaseConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return CaseConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return CaseConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return CaseConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return CaseConfig_1.outs;
    }
    static get in() {
        return CaseConfig_1.ins[0];
    }
    static get out() {
        return CaseConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new CaseConfig_1(baseVals.alias, parameters.getAppliedVals(this.conditions), baseVals.otherwise, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            alias: parameters.getStrVal(this.alias),
            conditions: this.conditions.map((condition) => condition.getBaseValues(parameters)),
            otherwise: parameters.getStrOrUndefVal(this.otherwise)
        };
    }
    static get info() {
        return new ToolConfigInfo(ToolType.CASE.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['switch'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const exp = tsb.export(this.out);
            const parameters = iConfigHelper.getParameters();
            const baseVals = this.getBaseValues(parameters);
            // get the set of column arguments used in all the case conditions to set as dependents
            const colArgs = new Set();
            const columnsIn = [];
            exp.getAllColumnMetadata().forEach((colMet) => columnsIn.push(colMet.column));
            // get cols from formula conditions
            const formulaConditions = this.conditions.filter((condition) => condition instanceof CaseCondition);
            formulaConditions.forEach((caseCond) => CaseCondition.getColumnArguments(iConfigHelper, caseCond, columnsIn).forEach((col) => colArgs.add(col)));
            // get cols from filter conditions
            const filterConditions = this.conditions.filter((condition) => condition instanceof CaseFilterCondition);
            filterConditions.forEach((cond) => cond.filter.getColumnArguments(parameters).forEach((col) => colArgs.add(col)));
            exp
                .copyAll(this.in)
                .makeNew(new Column(baseVals.alias, 'string'), this.getDependents(exp, this.in, Array.from(colArgs)), true)
                .initializeColumnFormats()
                .setColumnFormats(this.customFormats);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const parameters = iConfigHelper.getParameters();
        const baseVals = this.getBaseValues(parameters);
        const sourceValidationResult = validateSourceWithDetail(source, new Set(), [baseVals.alias]);
        if (!sourceValidationResult.isValid) {
            return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
        }
        const formulaConditions = this.conditions.filter((condition) => condition instanceof CaseCondition);
        const filterConditions = this.conditions.filter((condition) => condition instanceof CaseFilterCondition);
        const filtersValid = !filterConditions.some((filterGroup) => !filterGroup.filter.filters.every((f) => f.isValid(parameters, source)));
        const formulasValid = formulaConditions.every((c) => CaseCondition.isValid(iConfigHelper, c, source));
        if (filtersValid && formulasValid) {
            return new ToolValidationResult(ToolValidationState.VALID);
        }
        if (!filtersValid && !formulasValid) {
            return new ToolValidationResult(ToolValidationState.BAD_METADATA, 'formula and filter is invalid');
        }
        if (!filtersValid) {
            return new ToolValidationResult(ToolValidationState.BAD_METADATA, 'filter is invalid');
        }
        return new ToolValidationResult(ToolValidationState.BAD_METADATA, 'formula is invalid');
    }
    isValidForCalcDetail(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return new FormulaValidationInfo(ToolValidationState.BAD_METADATA, 'The syntax has an error. Check for incorrect placement of functions, operators and constraints.', FormulaValidationError.SYNTAX_ERROR);
        }
        const parameters = iConfigHelper.getParameters();
        const baseVals = this.getBaseValues(parameters);
        if (!isSourceValid(source, new Set(), [baseVals.alias])) {
            return new FormulaValidationInfo(ToolValidationState.BAD_METADATA, 'The Source is invalid', FormulaValidationError.INVALID_SOURCE);
        }
        const formulaConditions = this.conditions.filter((condition) => condition instanceof CaseCondition);
        const detail = formulaConditions.find((c) => CaseCondition.isValidDetail(iConfigHelper, c, source).validationState !== ToolValidationState.VALID);
        if (detail) {
            return CaseCondition.isValidDetail(iConfigHelper, detail, source);
        }
        return new FormulaValidationInfo(ToolValidationState.VALID);
    }
};
CaseConfig = CaseConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.CASE)
], CaseConfig);
export { CaseConfig };
//# sourceMappingURL=caseConfig.js.map